import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Users, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CrashMultiplayer() {
  const { user, token, refreshUser } = useAuth();
  const [gameData, setGameData] = useState({
    state: 'waiting',
    multiplier: 1.0,
    crashPoint: null,
    countdown: 20,
    bets: [],
    history: [],
    myBet: null
  });
  const [betAmount, setBetAmount] = useState('100');
  const [autoCashout, setAutoCashout] = useState('2.0');
  const [loading, setLoading] = useState(false);
  const lastRoundIdRef = useRef(null);
  const pollInterval = useRef(null);

  // Destructure for easier access
  const { state: gameState, multiplier, crashPoint, countdown, bets, history, myBet } = gameData;

  // Set page title
  useEffect(() => {
    document.title = 'RBX.Casino | Crash';
  }, []);

  // Fetch game state from backend
  const fetchGameState = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/multiplayer/crash/state`);
      const data = response.data;
      
      const newRoundId = data.round_id;
      const newState = data.state;
      
      // Detect new round by round_id change
      const isNewRound = newRoundId !== lastRoundIdRef.current;
      if (isNewRound) {
        lastRoundIdRef.current = newRoundId;
        refreshUser?.();
      }
      
      // Format bets for display
      const formattedBets = [];
      for (const [oduserId, betData] of Object.entries(data.bets || {})) {
        formattedBets.push({
          oduserId,
          username: betData.username,
          amount: betData.amount,
          cashed_out: betData.cashed_out,
          cashout_multiplier: betData.cashout_multiplier
        });
      }

      // Check if user has a bet in the current round
      let userBet = null;
      if (user && data.bets && data.bets[user.user_id]) {
        const bet = data.bets[user.user_id];
        userBet = {
          amount: bet.amount,
          auto_cashout: bet.auto_cashout,
          cashed_out: bet.cashed_out
        };
      }

      // Single atomic state update to prevent flickering
      setGameData(prev => {
        // Determine new state values
        let newMultiplier = 1.0;
        let newCrashPoint = null;
        let newCountdown = data.countdown || 20;
        
        // IMPORTANT: Preserve myBet if we have one and it's the same round
        // Only clear myBet when a new round starts AND we don't have a bet in the new round
        let newMyBet;
        if (isNewRound) {
          // New round - use the bet from server data (if any) or null
          newMyBet = userBet;
        } else {
          // Same round - keep existing bet if server doesn't have one (race condition protection)
          newMyBet = userBet || prev.myBet;
        }

        if (newState === 'betting' || newState === 'waiting') {
          newMultiplier = 1.0;
          newCrashPoint = null;
        } else if (newState === 'running') {
          newMultiplier = data.multiplier || 1.0;
          newCrashPoint = null;
        } else if (newState === 'crashed') {
          newMultiplier = data.multiplier;
          newCrashPoint = data.multiplier;
        }

        return {
          state: newState,
          multiplier: newMultiplier,
          crashPoint: newCrashPoint,
          countdown: newCountdown,
          bets: formattedBets,
          history: data.history || [],
          myBet: newMyBet
        };
      });
      
    } catch (error) {
      console.error('Error fetching crash state:', error);
    }
  }, [user, refreshUser]);

  // Start polling on mount
  useEffect(() => {
    fetchGameState(); // Initial fetch
    pollInterval.current = setInterval(fetchGameState, 200); // Poll every 200ms for smooth updates

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [fetchGameState]);

  const placeBet = async () => {
    if (!token) {
      toast.error('Please login to play');
      return;
    }
    if (gameState !== 'betting') {
      toast.error('Wait for next round');
      return;
    }

    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Invalid bet amount');
      return;
    }

    if (amount > (user?.site_balance || 0)) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      const autoVal = autoCashout ? parseFloat(autoCashout) : null;
      await axios.post(
        `${API_URL}/api/multiplayer/crash/bet`,
        null,
        {
          params: { amount, auto_cashout: autoVal },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setGameData(prev => ({
        ...prev,
        myBet: { amount, auto_cashout: autoVal, cashed_out: false }
      }));
      toast.success('Bet placed!');
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to place bet');
    } finally {
      setLoading(false);
    }
  };

  const cashout = async () => {
    if (!token || !myBet || myBet.cashed_out) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/multiplayer/crash/cashout`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGameData(prev => ({
        ...prev,
        myBet: prev.myBet ? { ...prev.myBet, cashed_out: true } : null
      }));
      toast.success(`Cashed out! +R$ ${response.data.payout.toFixed(0)} at ${response.data.multiplier}x`);
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cash out');
    }
  };

  const getMultiplierColor = () => {
    if (gameState === 'crashed') return 'text-red-500';
    if (multiplier >= 10) return 'text-purple-400';
    if (multiplier >= 5) return 'text-yellow-400';
    if (multiplier >= 2) return 'text-green-400';
    return 'text-blue-400';
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="font-display text-4xl font-bold text-white mb-2">
          <span className="text-green-400">Crash</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Game Display */}
        <div className="lg:col-span-3 glass rounded-2xl p-6 relative min-h-[400px]">
          {/* Multiplier */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            {gameState === 'crashed' && crashPoint ? (
              <>
                <div className="font-display text-7xl font-black text-red-500">
                  {crashPoint.toFixed(2)}x
                </div>
                <div className="text-2xl text-red-400 mt-2">CRASHED!</div>
              </>
            ) : gameState === 'running' ? (
              <>
                <div className={`font-display text-7xl font-black ${getMultiplierColor()}`}>
                  {multiplier.toFixed(2)}x
                </div>
                <div className="text-lg text-green-400 mt-2 flex items-center justify-center gap-2">
                  <TrendingUp className="animate-pulse" size={20} />
                </div>
              </>
            ) : (
              <>
                <div className="font-display text-7xl font-black text-blue-400">
                  1.00x
                </div>
                <div className="text-2xl text-slate-400 mt-2">
                  Starting in {countdown}s
                </div>
              </>
            )}
          </div>

          {/* History */}
          <div className="absolute top-4 left-4 flex gap-2 flex-wrap max-w-[70%]">
            {history.slice(-10).map((h, i) => (
              <div
                key={h.round_id || `crash-history-${i}`}
                className={`px-2 py-1 rounded text-xs font-mono ${
                  h.crash_point >= 2 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}
              >
                {h.crash_point?.toFixed(2)}x
              </div>
            ))}
          </div>

          {/* Players betting */}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-2">
              <Users size={16} className="text-slate-400" />
              <span className="text-slate-400 text-sm">{bets.length} players</span>
            </div>
            <div className="flex gap-2 flex-wrap max-h-20 overflow-y-auto">
              {bets.map((bet, i) => (
                <div
                  key={i}
                  className={`px-3 py-1 rounded-lg text-xs ${
                    bet.cashed_out
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-slate-800 text-slate-300'
                  }`}
                >
                  <span className="font-medium">{bet.username}</span>
                  <span className="ml-2">R${bet.amount}</span>
                  {bet.cashed_out && <span className="ml-1">@{bet.cashout_multiplier?.toFixed(2)}x</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Betting Panel */}
        <div className="glass rounded-2xl p-4">
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 text-sm">Bet Amount</Label>
              <Input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                className="input-default font-mono"
                disabled={gameState === 'running' || myBet}
              />
              <div className="flex gap-2 mt-2">
                {[50, 100, 500, 1000].map(amt => (
                  <button
                    key={amt}
                    onClick={() => setBetAmount(amt.toString())}
                    className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-xs hover:bg-slate-700"
                    disabled={gameState === 'running' || myBet}
                  >
                    {amt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-slate-300 text-sm">Auto Cashout (optional)</Label>
              <Input
                type="number"
                value={autoCashout}
                onChange={(e) => setAutoCashout(e.target.value)}
                className="input-default font-mono"
                placeholder="2.0"
                disabled={gameState === 'running' || myBet}
              />
            </div>

            {user ? (
              <>
                {(gameState === 'betting' || gameState === 'waiting') && !myBet && (
                  <Button
                    onClick={placeBet}
                    disabled={loading}
                    className="w-full btn-primary py-4"
                  >
                    {loading ? <Loader2 className="animate-spin" /> : 'Place Bet'}
                  </Button>
                )}

                {gameState === 'running' && myBet && !myBet.cashed_out && (
                  <Button
                    onClick={cashout}
                    className="w-full bg-green-600 hover:bg-green-500 py-4 text-lg font-bold animate-pulse"
                  >
                    Cash Out @ {multiplier.toFixed(2)}x
                  </Button>
                )}

                {myBet && myBet.cashed_out && (
                  <div className="p-3 rounded-lg bg-green-500/20 text-center">
                    <p className="text-green-400 font-bold">Cashed Out!</p>
                  </div>
                )}

                {(gameState === 'betting' || gameState === 'waiting') && myBet && !myBet.cashed_out && (
                  <div className="p-3 rounded-lg bg-blue-500/20 text-center">
                    <p className="text-blue-400 font-bold">Bet Placed: R${myBet.amount}</p>
                    <p className="text-slate-400 text-sm">Waiting for round to start...</p>
                  </div>
                )}

                {gameState === 'crashed' && (
                  <div className="p-3 rounded-lg bg-slate-800 text-center">
                    <p className="text-slate-400">Waiting for next round...</p>
                  </div>
                )}

                <div className="text-center text-slate-500 text-sm pt-2 border-t border-slate-700">
                  Balance: R$ {user.site_balance?.toLocaleString()}
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <p className="text-center text-slate-400 text-sm">Login to play</p>
                <a href="/login" className="block btn-primary text-center py-3 rounded-lg">Sign In</a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
